import * as React from 'react';
import { Fragment, ReactElement, useEffect } from 'react';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../../store';
import { Dispatch } from 'redux';
import {
  saveWeightRefImportInfo,
  fetchReferenceWeightsFolderPath,
  uploadRefWeightsFile,
  processCSVImport,
  fetchReferenceWeightsFilePath,
} from '../../store/ReferenceWeights/ReferenceWeightsActions';
import {
  ReferenceWeightsImportInfo,
  ReferenceWeightsImportRecords,
  UpdateWeightRefInfo,
} from '../../Interfaces/ReferenceWeightsImport';
import { Button } from '@progress/kendo-react-buttons';
import CSVReader from 'react-csv-reader';
import { IFileInfo } from 'react-csv-reader';
import { ReferenceWeightsFolderPath } from '../../Interfaces/ReferenceWeights/ReferenceWeightsFolderPath';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import { CSVUploadStatus } from '../../Interfaces/ReferenceWeights/CSVUploadStatus';
import { ReferenceWeightsFilePath } from '../../Interfaces/ReferenceWeightsFilePath';

// Constants
import {
  REFERENCE_WEIGHTS,
  FAILED_TO_LOAD_THE_FILE,
  STORE,
  LANE,
  EXPORT_DATETIME,
  IMPORT_FAIL_TEXT,
  UPLOADING,
} from '../../utils/constants';
import { GetString } from '../StringResourceManager';
import { Loader } from '@progress/kendo-react-indicators';

// Styles
const useStylesReferenceWeights = makeStyles((theme: Theme) =>
  createStyles({
    widgetBox: {
      margin: theme.spacing(2),
    },
    paperGrid: {
      marginTop: theme.spacing(7),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column',
    },
    margin: {
      margin: theme.spacing(1),
      width: '25ch',
    },
    textField: {
      width: '25ch',
    },
    helperText: {
      width: '35ch',
    },
    dialogCenterItems: {
      margin: '10px',
      textAlign: 'center',
    },
  }),
);

const ReferenceWeightsManagement: React.FC = (): ReactElement => {
  const classes = useStylesReferenceWeights();
  const [isFileLoadedSuccess, setFileLoadedStatus] = React.useState(false);
  const [importStatusInfo, setImportStatusInfo] = React.useState('');
  const [isFileUploading, setFileUploadingStatus] = React.useState(false);
  const [importInfo, setImportInfo] =
    React.useState<ReferenceWeightsImportInfo>(Object);
  const [weightsRefInfo, setWeightsRefInfo] =
    React.useState<UpdateWeightRefInfo[]>(Object);
  const [CSVFileName, setCSVFileName] = React.useState('');
  const [importResultMessage, setImportResultMessage] = React.useState('');
  const [isImportSuccess, setImportProcessStatus] =
    React.useState<any>(undefined);

  const dispatch: Dispatch<any> = useDispatch();

  const importRecords: ReferenceWeightsImportRecords = useSelector(
    (state: ApplicationState) =>
      state.referenceWeightsReducer.weightRefImportRecords,
  );

  const referenceWeightsFolderPath: ReferenceWeightsFolderPath = useSelector(
    (state: ApplicationState) => state.referenceWeightsReducer.folderPath,
  );

  const CSVUploadStatus: CSVUploadStatus = useSelector(
    (state: ApplicationState) => state.referenceWeightsReducer.CSVUploadStatus,
  );

  const CSVImportStatus: boolean | undefined = useSelector(
    (state: ApplicationState) => state.referenceWeightsReducer.CSVImportStatus,
  );

  const isUploadingCSV: boolean = useSelector(
    (state: ApplicationState) => state.referenceWeightsReducer.isUploadingCSV,
  );

  const isImportingCSV: boolean = useSelector(
    (state: ApplicationState) => state.referenceWeightsReducer.isImportingCSV,
  );

  const referenceWeightsFilePath: ReferenceWeightsFilePath = useSelector(
    (state: ApplicationState) =>
      state.referenceWeightsReducer.referenceWeightsFilePath,
  );

  const INVALID_FILE_FORMAT = GetString('INVALID_FILE_FORMAT');
  const READY_TO_UPLOAD = GetString('READY_TO_UPLOAD');
  const UPLOAD_FAILED = GetString('UPLOAD_FAILED');
  const UPLOAD_COMPLETED = GetString('UPLOAD_COMPLETED');
  const UPLOADING_CSV = GetString('UPLOADING_CSV');
  const SAVING_REFERENCE_WEIGHTS = GetString('SAVING_REFERENCE_WEIGHTS');
  const OK_BUTTON = GetString('OK_BUTTON');
  const CSV_IMPORT_TO_DB_ERROR_MESSAGE = GetString(
    'CSV_IMPORT_TO_DB_ERROR_MESSAGE',
  );
  const UNABLE_TO_LOCATE_WEIGHT_REF_FOLDER_PATH = GetString(
    'UNABLE_TO_LOCATE_WEIGHT_REF_FOLDER_PATH',
  );
  const NO_FILE_UPLOADED = GetString('NO_FILE_UPLOADED');

  const processFileData = (
    fileData: Array<any>,
    fileInfo: IFileInfo,
    originalFile?: File,
  ) => {
    setImportStatusInfo('');
    if (validFileNameFormat(fileInfo)) {
      let data: UpdateWeightRefInfo[] = [];
      fileData.forEach(fData => {
        if (fData[0] !== 'barcode' && fData[0] !== '' && Number(fData[1]))
          data.push({
            Barcode: fData[0],
            ReferenceWeight: Number(fData[1]),
          });
      });

      setWeightsRefInfo(data);
      setFileLoadedStatus(true);
      setImportStatusInfo(READY_TO_UPLOAD);
      setCSVFileName(fileInfo.name);
      resetUploadStatus();
    } else {
      setFileLoadedStatus(false);
      setImportStatusInfo(INVALID_FILE_FORMAT);
    }
  };

  const validFileNameFormat = (fileInfo: IFileInfo) => {
    let isValid = false;
    try {
      if (fileInfo.name.split('.')[1] !== 'csv') return;

      let splitData = fileInfo.name.split('_');

      let name = splitData[0];
      let storeNumber = Number(splitData[1]);
      let laneNumber = Number(splitData[2]);

      let timeData = fileInfo.name.split('T')[1];
      timeData = timeData.slice(0, timeData.length - 4).replace(/_/gi, ':');

      let dateData = splitData[3].slice(0, splitData[3].length - 2);

      let dateTimeExported = new Date(dateData + timeData);

      if (name !== REFERENCE_WEIGHTS) return;

      if (isNaN(Number(storeNumber)) && isNaN(Number(laneNumber))) return;

      if (dateTimeExported.toString() == 'Invalid Date') return;

      let fileNameData: ReferenceWeightsImportInfo = {
        StoreNumber: Number(splitData[1]),
        LaneNumber: Number(splitData[2]),
        DateTimeExported: new Date(dateData + timeData),
      };

      setImportInfo(fileNameData);

      isValid = true;
    } catch (e) {
      isValid = false;
    }
    return isValid;
  };

  const handleUploadClick = () => {
    if (referenceWeightsFolderPath.FilePath == '') {
      fetchSFTPFolderPath();
      setFileUploadingStatus(true);
      setImportStatusInfo(UPLOAD_FAILED);
      setImportResultMessage(UNABLE_TO_LOCATE_WEIGHT_REF_FOLDER_PATH);
      setImportProcessStatus(false);
    } else {
      uploadRefWeightsFile(
        dispatch,
        referenceWeightsFolderPath.FilePath + CSVFileName,
        weightsRefInfo,
      );
    }
  };

  const resetUploadStatus = () => {
    setImportProcessStatus(undefined);
    setImportResultMessage('');
    setFileUploadingStatus(false);
  };

  const handleOKClick = () => {
    resetUploadStatus();
  };

  useEffect((): ReturnType<React.EffectCallback> => {
    if (isFileLoadedSuccess) {
      if (isUploadingCSV) {
        setFileUploadingStatus(true);
        setImportStatusInfo(UPLOADING_CSV);
      }
    }
    return (): void => {};
  }, [isUploadingCSV]);

  useEffect((): ReturnType<React.EffectCallback> => {
    if (isFileLoadedSuccess) {
      if (CSVUploadStatus.IsSuccess) {
        processCSVImport(
          dispatch,
          referenceWeightsFolderPath.FilePath + CSVFileName,
        );
      } else if (CSVUploadStatus.IsSuccess === false) {
        setImportStatusInfo(UPLOAD_FAILED);
        setImportResultMessage(CSVUploadStatus.UploadErrorMessage);
        setImportProcessStatus(false);
      } else if (CSVUploadStatus.IsSuccess === undefined) {
        if (!isUploadingCSV) {
          resetUploadStatus();
        }
      }
    }
    return (): void => {};
  }, [CSVUploadStatus]);

  useEffect((): ReturnType<React.EffectCallback> => {
    if (isFileLoadedSuccess) {
      if (isImportingCSV) {
        setFileUploadingStatus(true);
        setImportStatusInfo(SAVING_REFERENCE_WEIGHTS);
      } else if (!isImportingCSV) {
        if (!CSVUploadStatus.IsSuccess) resetUploadStatus();
      }
    }
    return (): void => {};
  }, [isImportingCSV]);

  React.useEffect(() => {
    if (CSVImportStatus) {
      let records: ReferenceWeightsImportRecords = importRecords;
      records.Data.push(importInfo);
      saveWeightRefImportInfo(dispatch, records);
      fetchReferenceWeightsFilePath(dispatch);
      setImportStatusInfo(UPLOAD_COMPLETED);
      setImportResultMessage(CSVFileName);
      setImportProcessStatus(true);
    } else if (CSVImportStatus === false) {
      setImportStatusInfo(IMPORT_FAIL_TEXT);
      setImportResultMessage(CSV_IMPORT_TO_DB_ERROR_MESSAGE);
      setImportProcessStatus(false);
    } else if (CSVImportStatus === undefined) {
      if (!isImportingCSV) {
        setImportStatusInfo('');
        setFileUploadingStatus(false);
      }
    }
  }, [CSVImportStatus]);

  React.useEffect(() => {
    fetchReferenceWeightsFilePath(dispatch);
    fetchSFTPFolderPath();
  }, []);

  const fetchSFTPFolderPath = () => {
    fetchReferenceWeightsFolderPath(dispatch);
  };

  const onFileLoadError = () => {
    setImportStatusInfo(FAILED_TO_LOAD_THE_FILE);
  };

  return (
    <Fragment>
      <Typography variant="body1" color="primary">
        {GetString('REFERENCE_WEIGHTS_MANAGEMENT')}
      </Typography>
      &nbsp;
      <Typography variant="body2" color="textSecondary">
        {GetString('CURRENT_REFERENCE_WEIGHTS')}
      </Typography>
      <Typography variant="body2">
        {!referenceWeightsFilePath.FilePath
          ? NO_FILE_UPLOADED
          : referenceWeightsFilePath.FilePath.startsWith('sftp')
            ? referenceWeightsFilePath.FilePath.split('/').pop()
            : referenceWeightsFilePath.FilePath.split('\\').pop()}
      </Typography>
      &nbsp;
      <Typography variant="body2" color="textSecondary">
        {GetString('IMPORT_CSV')}
      </Typography>
      <FormControl variant="outlined">
        <CSVReader
          accept=".csv, text/csv, .tsv, test/tsv"
          onFileLoaded={processFileData}
          onError={onFileLoadError}
        />
        {isFileLoadedSuccess ? (
          <div>
            <FormHelperText className={classes.helperText}>
              {STORE + importInfo.StoreNumber}
            </FormHelperText>
            <FormHelperText className={classes.helperText}>
              {LANE + importInfo.LaneNumber}
            </FormHelperText>
            <FormHelperText className={classes.helperText}>
              {EXPORT_DATETIME + importInfo.DateTimeExported.toLocaleString()}
            </FormHelperText>
          </div>
        ) : null}
        {importStatusInfo == INVALID_FILE_FORMAT ? (
          <FormHelperText className={classes.helperText} error={true}>
            {importStatusInfo}
          </FormHelperText>
        ) : null}
        &nbsp;
        <Button
          disabled={!isFileLoadedSuccess || isFileUploading}
          onClick={() => handleUploadClick()}
        >
          {GetString('UPLOAD')}
        </Button>
        <div>
          {isFileUploading && (
            <Dialog>
              <p className={classes.dialogCenterItems}>{importStatusInfo}</p>
              <DialogActionsBar>
                <div className={classes.dialogCenterItems}>
                  {isImportSuccess ? (
                    <div>
                      <CheckCircleOutlineOutlinedIcon />
                      <p className={classes.dialogCenterItems}>
                        {importResultMessage}
                      </p>
                      <Button onClick={() => handleOKClick()}>
                        {OK_BUTTON}
                      </Button>
                    </div>
                  ) : isImportSuccess == false ? (
                    <div>
                      <ErrorIcon />
                      <p className={classes.dialogCenterItems}>
                        {importResultMessage}
                      </p>
                      <Button onClick={() => handleOKClick()}>
                        {OK_BUTTON}
                      </Button>
                    </div>
                  ) : isImportSuccess == undefined ? (
                    <Loader size="large" type={'infinite-spinner'} />
                  ) : null}
                </div>
              </DialogActionsBar>
            </Dialog>
          )}
        </div>
      </FormControl>
    </Fragment>
  );
};

export default ReferenceWeightsManagement;
